import { Link } from "react-router-dom";
import {
  ArrowUpRight, ShieldCheck, FileText, ScrollText, MapPin, ClipboardCheck,
  Building2, ScanLine, Phone, CalendarClock, Ruler, Compass, Award
} from "lucide-react";
import { COMPANY } from "@/lib/site";
import Seo from "@/components/Seo";

const HERO_PHOTO =
  "/img/hero.webp";

const STATS = [
  { kpi: "2.000+", label: "Projekata legalizacije", accent: true },
  { kpi: "20+", label: "Godina iskustva" },
  { kpi: "Dalmacija", label: "Od Zadra do Dubrovnika" },
  { kpi: "24h", label: "Odgovor na upit" },
];

const SERVICES = [
  { icon: Building2, title: "Obiteljske kuće", desc: "Legalizacija stambenih objekata do i preko 400 m² građevinske bruto površine.", type: "kuca" },
  { icon: ScanLine, title: "Pomoćne zgrade", desc: "Garaže, spremišta, ljetne kuhinje i dogradnje uz postojeće objekte.", type: "pomocna_zgrada" },
  { icon: MapPin, title: "Gospodarski objekti", desc: "Štale, plastenici, skladišta i prateći objekti poljoprivredne namjene.", type: "gospodarska_zgrada" },
  { icon: ClipboardCheck, title: "Geodetski elaborati", desc: "Snimka izvedenog stanja, parcelacijski elaborati i upis u katastar.", type: "drugo" },
];

const PROCESS_PREVIEW = [
  { n: "01", t: "Konzultacija", d: "Besplatna procjena izvedivosti u 24h." },
  { n: "02", t: "Snimka", d: "Geodetska i arhitektonska snimka izvedenog stanja." },
  { n: "03", t: "Postupak", d: "Predaja zahtjeva i zastupanje pred nadležnim tijelom." },
  { n: "04", t: "Rješenje", d: "Upis u katastar i zemljišnu knjigu." },
];

const MARQUEE = [
  "Geodetski elaborati",
  "Snimka izvedenog stanja",
  "Legalizacija u 30 dana",
  "Parcelacijski elaborati",
  "Upis u katastar i ZK",
  "Iskolčenja građevina",
  "Punomoć za zastupanje",
  "Fiksna ponuda bez skrivenih troškova",
];

export default function HomePage() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LocalBusiness",
        name: COMPANY.name,
        image: "https://legalizacija-kuca-dalmacija.hr/img/infokarta-logo.jpg",
        url: "https://legalizacija-kuca-dalmacija.hr/",
        telephone: COMPANY.phone,
        email: COMPANY.email,
        address: {
          "@type": "PostalAddress",
          streetAddress: "Visoka 59",
          addressLocality: "Split",
          postalCode: "21000",
          addressCountry: "HR",
        },
        areaServed: "Dalmacija",
      },
      {
        "@type": "Service",
        name: "Legalizacija bespravno izgrađenih objekata",
        provider: { "@type": "LocalBusiness", name: COMPANY.name },
        areaServed: "Dalmacija",
        serviceType: "Legalizacija objekata",
      },
      {
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "Koliko traje postupak legalizacije?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Tipično trajanje upravnog postupka je od 30 do 90 dana, ovisno o predmetu i nadležnom tijelu.",
            },
          },
          {
            "@type": "Question",
            name: "Za koje objekte radite legalizaciju?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Radimo legalizaciju obiteljskih kuća, pomoćnih i gospodarskih objekata te pripadajućih geodetskih elaborata.",
            },
          },
        ],
      },
    ],
  };

  return (
    <div data-testid="home-page">
      <Seo
        title="Legalizacija kuća u Dalmaciji"
        description="Legalizacija bespravno izgrađenih objekata u Dalmaciji. Infokarta d.o.o. iz Splita vodi cijeli postupak od snimke do upisa u katastar i zemljišne knjige."
        path="/"
        jsonLd={jsonLd}
      />
      {/* HERO - bright, with photo contained as a card on the right */}
      <section className="relative overflow-hidden border-b border-hair" style={{ backgroundColor: "#FBF7F2" }}>
        {/* Subtle warm decoration */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full" style={{ background: "radial-gradient(circle, rgba(217,45,32,0.06) 0%, transparent 70%)" }} />
          <div className="absolute bottom-0 right-0 w-[640px] h-[640px] rounded-full" style={{ background: "radial-gradient(circle, rgba(217,45,32,0.04) 0%, transparent 70%)" }} />
          <div className="absolute inset-0 grid-lines opacity-[0.05]" />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pt-20 pb-20 lg:pt-28 lg:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            {/* LEFT — text */}
            <div className="lg:col-span-7 reveal">
              <div className="overline text-ink-soft flex items-center gap-3">
                <span className="w-8 h-px bg-ink/30" /> Infokarta d.o.o. · Split
              </div>
              <h1 className="font-display text-ink text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] font-bold mt-6">
                Legalizacija<br />
                <span className="relative inline-block">
                  <span className="text-ink-soft">bespravno izgrađenih</span>
                  <span className="absolute -bottom-1 left-0 right-0 h-1 bg-[#D92D20]" aria-hidden="true" />
                </span><br />
                objekata u Dalmaciji.
              </h1>
              <div className="mt-6 inline-flex items-center gap-2 border border-ink/15 bg-white px-3 py-1.5">
                <MapPin className="w-3.5 h-3.5 text-[#D92D20]" strokeWidth={2} />
                <span className="font-brand uppercase text-[11px] tracking-[0.22em] text-ink">
                  Djelokrug rada · samo Dalmacija — od Zadra do Dubrovnika
                </span>
              </div>
              <p className="text-ink-soft text-lg max-w-2xl mt-8 leading-relaxed">
                Znamo da iza svake kuće stoji priča — naslijeđe djedova, mali san obitelji,
                godine truda. Pomažemo vam da ta priča dobije sve papire koje treba.
                <span className="block mt-3 text-ink/70">
                  Jedan poziv. Ostalo prepustite nama.
                </span>
              </p>
              <div className="flex flex-wrap gap-3 mt-10">
                <Link
                  to="/prijava"
                  data-testid="hero-cta-primary"
                  className="inline-flex items-center gap-2 bg-[#D92D20] text-white px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-[#B91C1C] transition-colors"
                >
                  Prijavi se
                  <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
                </Link>
                <Link
                  to="/postupak-legalizacije"
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center gap-2 border border-ink/30 text-ink px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-ink hover:text-white transition-colors"
                >
                  Kako izgleda postupak
                </Link>
                <a
                  href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
                  className="inline-flex items-center gap-2 text-ink px-2 py-3.5 text-sm font-medium hover:text-[#D92D20] transition-colors"
                  data-testid="hero-phone-link"
                >
                  <Phone className="w-4 h-4" strokeWidth={1.75} />
                  {COMPANY.phone}
                </a>
              </div>
            </div>

            {/* RIGHT — contained image card + pravni okvir mini card */}
            <div className="lg:col-span-5 reveal" style={{ animationDelay: "180ms" }}>
              <div className="relative">
                {/* Image card */}
                <div className="relative aspect-[4/5] overflow-hidden border border-hair shadow-xl">
                  <img
                    src={HERO_PHOTO}
                    alt="Aerial pogled na dalmatinske crvene krovove"
                    className="w-full h-full object-cover"
                    style={{ filter: "saturate(1.08)" }}
                  />
                  {/* Novi zakon badge inside image */}
                  <div className="absolute top-5 left-5 bg-white/95 backdrop-blur-sm px-3.5 py-2.5 border-l-2 border-[#D92D20]">
                    <div className="font-brand uppercase text-[#D92D20] text-[10px] tracking-[0.25em] font-bold">Novi zakon</div>
                    <div className="font-display text-ink text-3xl font-bold tracking-tighter leading-none mt-0.5">
                      2026<span className="text-[#D92D20]">.</span>
                    </div>
                  </div>
                  {/* Bottom legal note overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-5 text-white" style={{ background: "linear-gradient(180deg, transparent 0%, rgba(10,25,47,0.88) 60%, rgba(10,25,47,0.95) 100%)" }}>
                    <div className="font-brand uppercase text-[10px] tracking-[0.25em] text-white/80">Pravni okvir</div>
                    <p className="text-white text-sm leading-snug mt-2">
                      NN 48/2026 — ukinut prekluzivni rok za podnošenje zahtjeva.
                    </p>
                    <Link
                      to="/zakonski-propisi"
                      className="inline-flex items-center gap-1.5 mt-3 text-white text-xs font-medium border-b border-white/40 pb-0.5 hover:border-white"
                    >
                      Pročitaj propise
                      <ArrowUpRight className="w-3 h-3" strokeWidth={1.75} />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats strip - now on light theme */}
        <div className="relative border-t border-hair bg-white">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-2 md:grid-cols-4">
            {STATS.map((s, i) => (
              <div
                key={s.label}
                className={`py-7 ${i < STATS.length - 1 ? "md:border-r" : ""} border-hair ${
                  i % 2 === 0 ? "border-r md:border-r" : ""
                }`}
              >
                <div
                  className={`font-display text-4xl sm:text-5xl tracking-tighter font-bold ${
                    s.accent ? "text-[#D92D20]" : "text-ink"
                  }`}
                >
                  {s.kpi}
                </div>
                <div className="text-ink-soft text-xs uppercase tracking-[0.15em] mt-2">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MARQUEE TICKER */}
      <section className="relative bg-ink text-white overflow-hidden border-b border-ink" aria-hidden="true">
        <div className="flex whitespace-nowrap py-4 marquee-track">
          {[...MARQUEE, ...MARQUEE].map((m, i) => (
            <div key={i} className="flex items-center gap-6 px-8">
              <span className="font-brand uppercase text-sm tracking-[0.2em] text-white/85">{m}</span>
              <span className="text-[#D92D20] text-xl leading-none">◆</span>
            </div>
          ))}
        </div>
        <style>{`
          .marquee-track { animation: marquee 38s linear infinite; }
          @keyframes marquee { to { transform: translateX(-50%); } }
          @media (prefers-reduced-motion: reduce) { .marquee-track { animation: none; } }
        `}</style>
      </section>

      {/* WARM REASSURANCE - big number */}
      <section className="border-b border-hair" style={{ backgroundColor: "#FBF7F2" }}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="overline text-[#D92D20]">Brojke koje govore</div>
            <div className="mt-4 flex items-end gap-4 flex-wrap">
              <span className="font-display text-[120px] sm:text-[180px] lg:text-[220px] leading-[0.85] tracking-tighter font-bold text-ink">
                2.000
              </span>
              <span className="font-display text-6xl sm:text-7xl text-[#D92D20] font-bold leading-none">+</span>
            </div>
            <div className="font-brand uppercase tracking-[0.2em] text-ink-soft mt-4">
              uspješno provedenih postupaka legalizacije
            </div>
          </div>
          <div className="md:col-span-5 space-y-5">
            {[
              { icon: Award, t: "20+ godina stručnosti", d: "Ured posluje od 2002. godine." },
              { icon: Compass, t: "Geodezija + legalizacija", d: "Sve usluge pod jednim krovom — manje koordinacije, brži postupak." },
              { icon: Ruler, t: "Točnost prvo", d: "Snimka izvedenog stanja i elaborati izrađeni po pravilima struke." },
            ].map((row) => {
              const Icon = row.icon;
              return (
                <div key={row.t} className="flex items-start gap-4 bg-white border border-hair p-5 hover-lift">
                  <div className="w-10 h-10 bg-ink text-white flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="font-display font-semibold text-ink">{row.t}</div>
                    <div className="text-ink-soft text-sm mt-1 leading-relaxed">{row.d}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* BENTO services */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="home-services">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7">
            <div className="overline text-ink-soft">Usluge</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-semibold text-ink mt-4 leading-[1.05]">
              Kompletna podrška u<br />postupku legalizacije.
            </h2>
          </div>
          <div className="md:col-span-5 text-ink-soft leading-relaxed">
            Tim arhitekata, geodeta i pravnika vodi vas kroz svaki korak — od pripreme
            dokumentacije do upisa u katastar i zemljišne knjige.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mt-14">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            const span = ["md:col-span-7", "md:col-span-5", "md:col-span-5", "md:col-span-7"][i];
            const isAccent = i === 0;
            return (
              <Link
                key={s.title}
                to={`/prijava?vrsta=${s.type}`}
                className={`${span} group border border-hair p-8 lg:p-10 hover-lift bg-white relative overflow-hidden block`}
                data-testid={`service-card-${i}`}
              >
                <div className="absolute top-0 left-0 h-1 w-0 bg-[#D92D20] group-hover:w-full transition-[width] duration-500" />
                <div className="flex items-start justify-between">
                  <div className={`w-12 h-12 ${isAccent ? "bg-[#D92D20] text-white" : "bg-ink text-white"} flex items-center justify-center`}>
                    <Icon className="w-6 h-6" strokeWidth={1.5} />
                  </div>
                  <span className="font-display text-5xl text-ink/10 font-bold leading-none">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="font-display text-2xl font-semibold text-ink tracking-tight mt-10">
                  {s.title}
                </h3>
                <p className="text-ink-soft text-[15px] leading-relaxed mt-3 max-w-md">
                  {s.desc}
                </p>
                <div className="mt-6 inline-flex items-center gap-1.5 text-ink text-sm font-medium opacity-70 group-hover:opacity-100 transition-opacity">
                  Prijavi se
                  <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.75} />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* PROCESS PREVIEW */}
      <section className="bg-ink text-white border-t border-ink noise relative">
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-7">
              <div className="overline text-[#F4A8A0]">Kako to ide</div>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-semibold mt-4 leading-[1.05] text-white">
                Od vašeg poziva do<br />upisa u katastar.
              </h2>
            </div>
            <div className="md:col-span-5 text-white/75 leading-relaxed">
              Tipično trajanje cijelog postupka je 30 dana. Vi se javite —
              mi rješavamo papire, vi nastavljate sa svojim životom.
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 mt-12">
            {PROCESS_PREVIEW.map((p, i) => (
              <div key={p.n} className="bg-ink p-7 relative group">
                <div className="flex items-baseline gap-4">
                  <span className="font-display text-5xl tracking-tighter font-bold text-white/15">{p.n}</span>
                  <span className="h-px flex-1 bg-white/15 group-hover:bg-[#D92D20] transition-colors" />
                </div>
                <h3 className="font-display text-2xl font-semibold tracking-tight mt-6 text-white">{p.t}</h3>
                <p className="text-white/65 text-sm mt-2 leading-relaxed">{p.d}</p>
                <CalendarClock className="absolute bottom-7 right-7 w-4 h-4 text-white/30" strokeWidth={1.5} />
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-wrap gap-4 items-center">
            <Link
              to="/postupak-legalizacije"
              className="inline-flex items-center gap-2 bg-white text-ink px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-white/90 transition-colors"
            >
              Detaljan opis postupka
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
            </Link>
            <span className="text-white/50 text-sm">ili nas nazovite — odgovaramo isti dan</span>
          </div>
        </div>
      </section>

      {/* SPLIT block */}
      <section className="border-t border-hair">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-6">
            <div className="overline text-ink-soft">Zašto Infokarta</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-semibold text-ink mt-4 leading-[1.05]">
              Točnost, struka i<br />
              <span className="text-[#D92D20]">jasna komunikacija.</span>
            </h2>
            <p className="text-ink-soft mt-6 leading-relaxed max-w-lg">
              U Splitu smo od 2002. godine. Pružamo kompletne geodetske usluge
              i specijalizirani smo za legalizaciju objekata na obali i otocima —
              gdje je svaki katastarski metar važan.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-ink/10 mt-10 border border-ink/10">
              {[
                { icon: ShieldCheck, t: "Ovlašteni inženjeri", d: "Geodeti, arhitekti i građevinari s ovlaštenjima struke." },
                { icon: FileText, t: "Fiksna ponuda", d: "Bez skrivenih troškova — sve stavke unaprijed definirane." },
                { icon: ScrollText, t: "Pravna podrška", d: "Suradnja s odvjetničkim uredom u svim koracima." },
                { icon: ClipboardCheck, t: "Praćenje predmeta", d: "Uvid u status vašeg predmeta od početka do kraja." },
              ].map((b) => {
                const Icon = b.icon;
                return (
                  <div key={b.t} className="bg-white p-6">
                    <Icon className="w-5 h-5 text-ink" strokeWidth={1.5} />
                    <div className="font-display font-semibold text-ink mt-4">{b.t}</div>
                    <div className="text-ink-soft text-sm mt-1.5">{b.d}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="md:col-span-6 relative">
            <div className="relative aspect-[4/5] overflow-hidden border border-hair">
              <img
                src="/img/legalizacija.webp"
                alt="Mediteranska kuća"
                className="w-full h-full object-cover transition-transform duration-[1500ms] hover:scale-105"
              />
              <div className="absolute top-4 left-4 bg-white px-3 py-1.5 font-brand uppercase text-[10px] tracking-[0.2em] text-ink">
                Dalmacija · 2026.
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:block bg-[#D92D20] text-white p-6 max-w-xs shadow-xl">
              <div className="overline text-white/70">Lokacija</div>
              <div className="font-display text-2xl mt-2 leading-tight">{COMPANY.address}</div>
              <div className="text-white/80 text-sm mt-2">Radimo na području Dalmacije — od Zadra do Dubrovnika.</div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="border-t border-hair" style={{ backgroundColor: "#FBF7F2" }}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end mb-12">
            <div className="md:col-span-7">
              <div className="overline text-[#D92D20]">Što kažu naši klijenti</div>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-semibold text-ink mt-4 leading-[1.05]">
                Iza svake legalizacije<br />je jedna obitelj.
              </h2>
            </div>
            <div className="md:col-span-5 text-ink-soft leading-relaxed">
              Kad nam klijent kaže: „Konačno mi kuća više nije briga.“ — ponosni i sretni!
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                t: "Naslijeđena kuća djeda u zaleđu Splita — godinama smo se bojali da je nikad nećemo legalizirati. U Infokarti su nas vodili korak po korak, bez stresa.",
                a: "Ivana M.",
                m: "Kaštela",
              },
              {
                t: "Ljetnikovac na otoku, dograđen prije 20 godina. Mislili smo da je nemoguće. Silvio je sve objasnio na jednoj kavi i — gotovo.",
                a: "Marko P.",
                m: "Brač",
              },
              {
                t: "Fer cijena, brza komunikacija i — što mi je najvažnije — ljudi koji se javljaju na telefon. Pravo osvježenje.",
                a: "Ana K.",
                m: "Trogir",
              },
            ].map((q, i) => (
              <div key={i} className="bg-white border border-hair p-7 hover-lift relative">
                <div className="absolute -top-3 left-6 font-display text-6xl text-[#D92D20] leading-none">“</div>
                <p className="text-ink leading-relaxed text-[15px] mt-3">{q.t}</p>
                <div className="mt-6 pt-5 border-t border-hair">
                  <div className="font-display font-semibold text-ink">{q.a}</div>
                  <div className="text-ink-soft text-xs">{q.m}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BIG CONTACT CTA */}
      <section className="border-t border-hair bg-ink text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <div className="overline text-[#F4A8A0]">Kontaktirajte nas</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-semibold mt-4 leading-[1.05]">
              Recite nam u dvije rečenice<br />— i mi krećemo.
            </h2>
            <p className="text-white/75 mt-5 leading-relaxed max-w-xl">
              Ne morate imati sve papire ni znati pojmove. Imate kuću i pitanje — to je dovoljno.
              Javljamo se istog dana, bez obveze.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link
                to="/prijava"
                data-testid="home-bottom-cta"
                className="inline-flex items-center gap-2 bg-[#D92D20] text-white px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-[#B91C1C] transition-colors"
              >
                Prijavi se
                <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
              </Link>
              <a
                href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
                className="inline-flex items-center gap-2 border border-white/30 text-white px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-white/10 transition-colors"
              >
                <Phone className="w-4 h-4" strokeWidth={1.75} />
                {COMPANY.phone}
              </a>
            </div>
          </div>
          <div className="md:col-span-4 border-t md:border-t-0 md:border-l border-white/15 md:pl-10 pt-6 md:pt-0">
            <div className="overline text-white/50">Direktni kontakt</div>
            <a href={`mailto:${COMPANY.email}`} className="block font-display text-xl mt-3 hover:text-white/80 break-all">
              {COMPANY.email}
            </a>
            <div className="text-white/60 text-sm mt-4">{COMPANY.hours}</div>
            <div className="text-white/50 text-xs mt-3 leading-relaxed">
              Vrata su uvijek otvorena — svratite na kavu i pitajte što vas zanima.
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
